import React from "react";

import { useOutlooks } from "../../api/hooks/defs/outlook";

//services
import { OutlookColor } from "../../api/services";

//components
import InputWrapper from "../Inputs/InputWrapper";
import SelectInput, { Result } from "../Inputs/SelectInput";
import Typography from "../Text";
//css
import css from "./OutlookPicker.module.scss";

interface OutlookPickerProps {
	/**
	 * current value of the outlook picker
	 */
	value?: OutlookColor | OutlookColor[] | null;

	/**
	 * function runs on OutlookPicker change
	 */
	onChange?: (value: OutlookColor | OutlookColor[] | null) => void;

	/**
	 * label for the outlook picker
	 */
	label?: string;

	/**
	 * if true, the outlook picker will have a clear button
	 */
	isClearable?: boolean;

	/**
	 * error message to display below the outlook picker
	 */
	errorMessage?: string;

	animatedError?: boolean;

	/**
	 * the maximum height of the menu before scrolling
	 */
	maxMenuHeight?: number;

	/**
	 * the placeholder for the outlook picker
	 */
	placeholder?: string;

	/**
	 * if true, the outlook picker will allow multiple selections
	 */
	multiSelect?: boolean;
}

const OutlookPicker = ({
	value,
	onChange,
	label,
	isClearable = false,
	errorMessage,
	animatedError,
	maxMenuHeight,
	placeholder = "Select a goal outlook",
	multiSelect = false,
}: OutlookPickerProps) => {
	const { data, isLoading } = useOutlooks();
	const results =
		data?.colors?.map((color) => {
			return {
				label: color.name,
				value: color.id,
			};
		}) || [];

	/**
	 * The outlook option component is used to render the outlook options in the
	 * dropdown menu.
	 */
	const OutlookOption = (result: Result) => {
		const outlook = data?.colors ? data?.colors.find((fetchedResult) => result?.value === fetchedResult.id) : null;
		return (
			<div className={css.outlookOption}>
				{outlook?.color && <div className={css.outlookColor} style={{ backgroundColor: outlook?.color }} />}
				<Typography size="paragraph-3" weight="regular" color="neutral-9" className={css.outlookTitle}>
					{result?.label}
				</Typography>
			</div>
		);
	};

	const getValue = (value: OutlookColor | OutlookColor[] | null) => {
		if (multiSelect && value) {
			return value.map((outlook) => {
				return {
					value: outlook.id,
					label: outlook.name,
				};
			});
		} else if (value) {
			return {
				value: value.id,
				label: value.name,
			};
		}
		return null;
	};

	return (
		<div data-testid="OutlookPicker">
			<InputWrapper label={label} errorMessage={errorMessage} animatedError={animatedError}>
				<SelectInput
					placeholder={placeholder}
					inputId="outlook-picker"
					isLoading={isLoading}
					results={results}
					value={getValue(value)}
					// if multiselect then outlookAsResult will be an array of results else it is result
					onChange={(outlookAsResult: Result | Result[]) => {
						if (!outlookAsResult || !onChange) {
							onChange && onChange(null);
							return;
						}

						if (multiSelect) {
							const result = outlookAsResult as Result[];
							const colors = data?.colors.filter((color) => {
								return result?.some((outlook) => outlook.value === color.id);
							});
							onChange(colors);
						} else {
							const result = outlookAsResult as Result;
							const color = data?.colors.find((outlook) => outlook.id === result?.value);
							onChange(color);
						}
					}}
					isClearable={isClearable}
					formatOptionLabel={(outlook: Result) => OutlookOption(outlook)}
					maxMenuHeight={maxMenuHeight}
					isMultiSelect={multiSelect}
				/>
			</InputWrapper>
		</div>
	);
};

export default OutlookPicker;
